import React, { useState } from 'react';
import { PhoneCall, MapPin, CalendarPlus, Loader2 } from 'lucide-react';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { toast } from 'sonner';
import api from '../lib/api';

const TYPES = [
  { value: 'call', label: 'Follow-up Call', icon: PhoneCall, active: 'bg-green-50 border-green-300 text-green-700' },
  { value: 'site_visit', label: 'Site Visit', icon: MapPin, active: 'bg-blue-50 border-blue-300 text-blue-700' },
];

const tomorrow = () => { const d = new Date(); d.setDate(d.getDate() + 1); return d.toISOString().slice(0, 10); };

export const LeadActivityScheduler = ({ lead, agentName }) => {
  const [type, setType] = useState('call');
  const [date, setDate] = useState(tomorrow());
  const [time, setTime] = useState('');
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (!date) { toast.error('Pick a date'); return; }
    setSaving(true);
    const label = TYPES.find(t => t.value === type).label;
    const payload = {
      title: `${label} — ${lead.name}`,
      task_type: type,
      due_date: time ? `${date}T${time}:00` : date,
      due_time: time || null,
      priority: type === 'site_visit' ? 'high' : 'medium',
      notes,
      lead_id: lead.id,
      lead_name: lead.name,
      lead_phone: lead.phone,
    };
    if (lead.assigned_to) { payload.assigned_to = lead.assigned_to; payload.assigned_agent_name = agentName; }
    try {
      await api.post('/tasks/create', payload);
      toast.success(`${label} scheduled for ${date}`);
      setNotes(''); setTime(''); setDate(tomorrow());
    } catch { toast.error('Failed to schedule'); }
    finally { setSaving(false); }
  };

  return (
    <Card className="p-4" data-testid="lead-activity-scheduler">
      <h3 className="font-semibold text-slate-900 mb-3 flex items-center gap-2">
        <CalendarPlus className="w-4 h-4 text-slate-400" /> Schedule Activity
      </h3>
      <div className="grid grid-cols-2 gap-2 mb-3">
        {TYPES.map(t => (
          <button key={t.value} type="button" onClick={() => setType(t.value)} data-testid={`schedule-type-${t.value}`}
            className={`flex items-center justify-center gap-1.5 h-9 rounded-lg border text-xs font-medium transition-colors ${type === t.value ? t.active : 'border-slate-200 text-slate-500 hover:bg-slate-50'}`}>
            <t.icon className="w-3.5 h-3.5" /> {t.label}
          </button>
        ))}
      </div>
      <div className="grid grid-cols-2 gap-2 mb-2">
        <Input type="date" value={date} min={new Date().toISOString().slice(0, 10)} onChange={e => setDate(e.target.value)} className="h-9 text-sm" data-testid="schedule-date-input" />
        <Input type="time" value={time} onChange={e => setTime(e.target.value)} className="h-9 text-sm" data-testid="schedule-time-input" />
      </div>
      <Input placeholder="Note (optional)" value={notes} onChange={e => setNotes(e.target.value)} className="h-9 text-sm mb-3" data-testid="schedule-notes-input" />
      <Button size="sm" className="w-full bg-slate-900 hover:bg-slate-800" onClick={submit} disabled={saving} data-testid="schedule-activity-submit">
        {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Schedule'}
      </Button>
    </Card>
  );
};
